// ─── Edge swipe: open the menu, or go to a screen's home ─────────────────────
// Swiping in from the left edge does one of two things, decided by WHICH
// screen you're on — never by the page you happened to come from:
//
//   • Main screens (everything in the side menu, plus Browse / the dashboard):
//     the swipe opens the side menu. Going Profile → Browse and swiping on
//     Browse opens the menu; it does NOT take you back to Profile.
//   • Sub-screens that belong to one main screen (Edit Profile → Profile,
//     a chat → Matches / Chats, Create Listing → dashboard): the swipe goes to
//     that screen.
//   • Someone else's profile opens from many places (feed, chat, Likes), so it
//     has no single home; there the swipe returns to where you opened it from.
//   • Sign-in / onboarding steps ignore the swipe so no required step is skipped.
//
// A screen with unsaved work can veto the swipe with setBackGuard(fn) — fn
// returns (or resolves to) false to stay put. Edit Profile uses it.
import { useEffect, useRef } from 'react';

export const OPEN_MENU_EVENT = 'nomad:open-menu';

const NO_SWIPE_SCREENS = ['/', '/role-select', '/create-profile', '/verify-email',
  '/setup-search', '/email-confirmed', '/reset-password'];
const MAIN_SCREENS = ['/swipe', '/watchlist', '/matches', '/alerts', '/notifications',
  '/host-dashboard', '/blast', '/account-settings', '/beta-feedback'];

/**
 * What a left-edge swipe should do on `pathname`.
 * @returns {{type:'menu'}|{type:'home', path:string}|{type:'back'}|null}
 */
export function swipeTarget(pathname, { userId, role } = {}) {
  if (NO_SWIPE_SCREENS.includes(pathname)) return null;
  if (MAIN_SCREENS.includes(pathname)) return { type: 'menu' };
  if (pathname.startsWith('/profile/')) {
    // Your own profile is a main screen (it's in the menu); anyone else's
    // has no single home.
    return userId && pathname === `/profile/${userId}` ? { type: 'menu' } : { type: 'back' };
  }
  if (pathname === '/edit-profile') return { type: 'home', path: userId ? `/profile/${userId}` : '/swipe' };
  if (pathname === '/admin') return { type: 'home', path: '/edit-profile' };
  if (pathname.startsWith('/chat/')) {
    return { type: 'home', path: role === 'host' ? '/host-dashboard?tab=matches' : '/matches' };
  }
  if (pathname === '/create-listing') return { type: 'home', path: '/host-dashboard' };
  // About / Terms and anything else: back to where it was opened from.
  return { type: 'back' };
}

let guard = null;
/** Register (fn) or clear (null) a check that runs before the swipe leaves a screen. */
export function setBackGuard(fn) { guard = fn; }

const EDGE = 28;      // px from the left edge where the swipe must start
const TRIGGER = 70;   // px of horizontal travel to count
const MAX_DRIFT = 50; // px of vertical travel before we treat it as a scroll

/** Installs the document-level gesture. Call once, inside the Router. */
export function useEdgeSwipe(navigate, location, { userId, role, homePath }) {
  // The previous screen's full URL, so "go to home" can step back through
  // history (keeping that screen's scroll position) when home is exactly where
  // you came from, instead of stacking a new copy of it.
  const prevRef = useRef(null);
  const curRef = useRef(null);
  useEffect(() => {
    const here = location.pathname + location.search;
    if (curRef.current !== here) { prevRef.current = curRef.current; curRef.current = here; }
  }, [location.pathname, location.search]);

  const ctx = useRef({});
  ctx.current = { userId, role, homePath };

  useEffect(() => {
    let start = null;
    const onStart = (e) => {
      const t = e.touches[0];
      start = t && t.clientX <= EDGE ? { x: t.clientX, y: t.clientY } : null;
    };
    const onMove = async (e) => {
      if (!start) return;
      const t = e.touches[0];
      const dx = t.clientX - start.x;
      if (Math.abs(t.clientY - start.y) > MAX_DRIFT) { start = null; return; }
      if (dx < TRIGGER) return;
      start = null; // fire once per gesture

      const target = swipeTarget(window.location.pathname, ctx.current);
      if (!target) return;
      if (target.type === 'menu') { window.dispatchEvent(new Event(OPEN_MENU_EVENT)); return; }

      if (guard) {
        try { if ((await guard()) === false) return; } catch (_) { return; }
      }
      const idx = window.history.state?.idx ?? 0;
      if (target.type === 'home') {
        if (idx > 0 && prevRef.current === target.path) navigate(-1);
        else navigate(target.path, { replace: true });
      } else if (idx > 0) {
        navigate(-1);
      } else {
        navigate(ctx.current.homePath || '/', { replace: true });
      }
    };
    const onEnd = () => { start = null; };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [navigate]);
}
